import { lazy, Suspense } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LayoutDashboard, ListTodo, Clock, Inbox, Trophy, FileText, Settings, ShieldCheck, LogOut, MoonStar, Sun } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useTheme } from '@/lib/theme'
import { usePageViewTracking } from '@/lib/pageViewTracking'
import { NotificationsBell } from '@/components/NotificationsBell'
import { MobileNav } from '@/components/layout/MobileNav'

// Its own chunk — `motion` shouldn't weigh down every authenticated
// page just for a transition nicety (see docs/DECISIONS.md).
const PageTransition = lazy(() => import('@/components/layout/PageTransition'))

const NAV_ITEMS = [
  { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard, end: true },
  { to: '/tasks', labelKey: 'nav.tasks', icon: ListTodo, end: false },
  { to: '/time', labelKey: 'nav.time', icon: Clock, end: false },
  { to: '/requests', labelKey: 'nav.requests', icon: Inbox, end: false },
  { to: '/achievements', labelKey: 'nav.achievements', icon: Trophy, end: false },
  { to: '/reports', labelKey: 'nav.reports', icon: FileText, end: false },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings, end: false },
] as const

export function AppShell() {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const location = useLocation()

  usePageViewTracking(Boolean(user))

  const toggleLanguage = () => i18n.changeLanguage(i18n.resolvedLanguage === 'ar' ? 'en' : 'ar')
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark')

  // Operator-only — never rendered for the Manager, and the API route
  // behind it rejects non-operators regardless.
  const navItems = user?.role === 'operator'
    ? [...NAV_ITEMS, { to: '/activity', labelKey: 'nav.activity', icon: ShieldCheck, end: false }]
    : NAV_ITEMS

  return (
    <div className="min-h-screen bg-paper">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
        <div className="flex items-center gap-2.5">
          <img src="/brand/tar-group-logo.png" alt="TAR Group" className="h-6 w-auto" />
          <span className="font-display text-sm font-semibold tracking-wide">{t('app.name')}</span>
        </div>

        <nav className="hidden gap-1 rounded-pill bg-surface-2 p-1 sm:flex">
          {navItems.map(({ to, labelKey, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isActive ? 'bg-ink text-paper' : 'text-ink-soft hover:text-ink'
                }`
              }
            >
              <Icon size={14} strokeWidth={2} />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <NotificationsBell />
          <button
            type="button"
            onClick={toggleLanguage}
            className="rounded-md border border-line px-2.5 py-1.5 text-xs font-semibold text-ink-soft hover:text-ink"
            aria-label="Toggle language"
          >
            {i18n.resolvedLanguage === 'ar' ? 'EN' : 'AR'}
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-md border border-line p-1.5 text-ink-soft hover:text-ink"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={15} /> : <MoonStar size={15} />}
          </button>
          <button
            type="button"
            onClick={() => logout()}
            className="rounded-md border border-line p-1.5 text-ink-soft hover:text-critical"
            aria-label={t('auth.signOut')}
          >
            <LogOut size={15} />
          </button>
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand text-[11px] font-bold text-brand-ink">
            {user?.name?.slice(0, 2).toUpperCase()}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-6 pb-20 sm:pb-6">
        <Suspense fallback={<Outlet />}>
          <PageTransition pathKey={location.pathname}>
            <Outlet />
          </PageTransition>
        </Suspense>
      </main>

      <MobileNav />
    </div>
  )
}
